import React, { useState, useEffect, useCallback } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CWidgetStatsA,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CFormSelect,
  CSpinner,
  CButton,
  CButtonGroup,
  CTooltip,
  CProgress,
} from "@coreui/react";
import { CChartBar, CChartDoughnut } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import {
  cilFilter,
  cilReload,
  cilX,
  cilChartLine,
  cilChartPie,
  cilStorage,
} from "@coreui/icons";
import DownloadExcel from "src/reusables/DownloadExcel";
import StockFilter from "./StockFilter";
import { useNavigate } from "react-router-dom";
import { getStockSummary } from "src/services/Analytics";
import ls from "local-storage";
import SalesPicker from "src/components/SalesPicker";

const StockAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    store: "",
    category: "",
    stockStatus: "",
    timeRange: "monthly",
  });
  const [activeChart, setActiveChart] = useState("doughnut");
  const [topProductsByCategory, setTopProductsByCategory] = useState([]); // NEW STATE
  const navigate = useNavigate();

  const getFilterParams = useCallback(() => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");

    return {
      // dateFrom: Moment(dateRange[0].startDate).format("YYYY-MM-DD"),
      // dateTo: Moment(dateRange[0].endDate).format("YYYY-MM-DD"),
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId:
        prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId:
        city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  }, []);

  const fetchData = useCallback(async () => {
    const filters = getFilterParams();
    setLoading(true);
    try {
      const summaryResp = await getStockSummary({ ...filters, includeTopProducts: true });
      if (summaryResp.success) {
        setData(summaryResp.data);
        setTopProductsByCategory(summaryResp.data.topProductsByCategory || []);
      }
    } catch (error) {
      console.error("Error fetching stock analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [getFilterParams]);

  // removed monthly chart builder (moved to Stores analytics)

  useEffect(() => {
    fetchData();
  }, [filters, fetchData]);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setFilterOpen(false);
  };

  const handleResetFilters = () => {
    setFilters({
      store: "",
      category: "",
      stockStatus: "",
      timeRange: "monthly",
    });
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
  };

  const getFilterValueDisplay = (key, value) => {
    const displayMap = {
      store: value,
      category: value,
      stockStatus:
        {
          in_stock: "In Stock",
          out_of_stock: "Out of Stock",
          low_stock: "Low Stock",
        }[value] || value,
      timeRange:
        {
          weekly: "Last 7 Days",
          biweekly: "Last 14 Days",
          monthly: "Last 30 Days",
          quarterly: "Last 90 Days",
        }[value] || value,
    };
    return displayMap[key] || value;
  };

  // Calculate summary metrics from the API data
  const calculateSummary = () => {
    if (!data) return null;

    const totalProductsTracked = data.mostDiverseInventory.reduce(
      (sum, store) => sum + parseInt(store.unique_products),
      0
    );

    const productsInStock = data.highestStockVolume.reduce(
      (sum, store) => sum + parseInt(store.total_inventory),
      0
    );

    const outOfStockProducts = data.outOfProducts.reduce(
      (sum, store) => sum + parseInt(store.out_of_stock_items),
      0
    );

    return {
      totalProductsTracked,
      productsInStock,
      outOfStockProducts,
      averageStockPerStore: (
        productsInStock / data.mostDiverseInventory.length
      ).toFixed(1),
    };
  };

  const summaryData = calculateSummary();

  if (loading && !data) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "80vh" }}
      >
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  return (
    <div className="stock-analytics" style={{ color: "black" }}>
      <SalesPicker />
      {/* Filter Section */}
      <CCard className="mb-4">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <CCol>
            <h4>Stock Analytics Dashboard</h4>
            <small> *Menampilkan data berdasarkan kunjungan terakhir</small>
          </CCol>

          <div className="d-flex gap-2">
            <CTooltip content="Refresh Data">
              <CButton
                color="light"
                onClick={fetchData}
                disabled={loading}
                className="p-2"
                shape="pill"
              >
                <CIcon icon={cilReload} />
              </CButton>
            </CTooltip>
            <CTooltip content="Filter Data">
              <CButton
                color="light"
                onClick={() => setFilterOpen(true)}
                className="p-2"
                shape="pill"
                active={Object.values(filters).some((f) => f)}
              >
                <CIcon icon={cilFilter} />
              </CButton>
            </CTooltip>
          </div>
        </CCardHeader>

        {/* Active Filters */}
        {Object.keys(filters).some((key) => filters[key]) && (
          <CCardBody className="pt-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <small className="text-muted me-2">Active Filters:</small>
              {Object.entries(filters).map(
                ([key, value]) =>
                  value && (
                    <CBadge
                      key={key}
                      color="primary"
                      className="d-flex align-items-center py-2 px-3"
                      shape="rounded-pill"
                    >
                      <span className="me-2">
                        {key}: {getFilterValueDisplay(key, value)}
                      </span>
                      <div
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFilter(key);
                        }}
                        className="d-flex align-items-center"
                      >
                        <CIcon icon={cilX} width={14} />
                      </div>
                    </CBadge>
                  )
              )}
              <CButton
                color="link"
                size="sm"
                className="text-decoration-none p-0 ms-2"
                onClick={handleResetFilters}
              >
                Clear All
              </CButton>
            </div>
          </CCardBody>
        )}
      </CCard>

      {/* Summary Widgets */}
      {summaryData && (
        <CRow className="mb-4">
          <CCol xs={12} md={6} lg={3}>
            <CWidgetStatsA
              className="mb-4"
              color="primary"
              value={summaryData.totalProductsTracked}
              title="Total Products Tracked"
              icon={<CIcon icon={cilStorage} height={36} />}
            />
          </CCol>
          <CCol xs={12} md={6} lg={3}>
            <CWidgetStatsA
              className="mb-4"
              color="success"
              value={summaryData.productsInStock}
              title="Products In Stock"
              icon={<CIcon icon={cilChartPie} height={36} />}
            />
          </CCol>
          <CCol xs={12} md={6} lg={3}>
            <CWidgetStatsA
              className="mb-4"
              color="danger"
              value={summaryData.outOfStockProducts}
              title="Out of Stock"
              icon={<CIcon icon={cilChartLine} height={36} />}
            />
          </CCol>
          <CCol xs={12} md={6} lg={3}>
            <CWidgetStatsA
              className="mb-4"
              color="info"
              value={summaryData.averageStockPerStore}
              title="Avg Stock Per Store"
              icon={<CIcon icon={cilChartLine} height={36} />}
            />
          </CCol>
        </CRow>
      )}

{/* Top 10 Products by Total Stock for Each Category */}
{topProductsByCategory && topProductsByCategory.length > 0 && (
  <CRow className="mb-4">
    {topProductsByCategory.map((cat) => (
      <CCol xs={3} key={cat.category}>
        <CCard className="mb-4">
          <CCardHeader>
            <h5>Top 10 Products – {cat.category_name}</h5>
          </CCardHeader>
          <CCardBody>
            <CTable striped hover responsive>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>#</CTableHeaderCell>
                  <CTableHeaderCell>Product Name</CTableHeaderCell>
                  <CTableHeaderCell>Total Stock</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {cat.products
                  .slice(0, 10) // limit to top 10
                  .map((p, index) => (
                    <CTableRow key={p.product_id || index}>
                      <CTableDataCell>{index + 1}</CTableDataCell>
                      <CTableDataCell>{p.product_name}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge color="info">{p.total_stock}</CBadge>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
              </CTableBody>
            </CTable>
          </CCardBody>
        </CCard>
      </CCol>
    ))}
  </CRow>
)}


      {/* Top Stores by Product Diversity */}
      <CRow className="mb-4">
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <span>Stores with Most Product Variety</span>
              <CButtonGroup>
                <CButton
                  color={
                    activeChart === "doughnut" ? "primary" : "outline-secondary"
                  }
                  size="sm"
                  onClick={() => setActiveChart("doughnut")}
                >
                  Doughnut
                </CButton>
                <CButton
                  color={
                    activeChart === "bar" ? "primary" : "outline-secondary"
                  }
                  size="sm"
                  onClick={() => setActiveChart("bar")}
                >
                  Bar
                </CButton>
              </CButtonGroup>
            </CCardHeader>
            <CCardBody>
              {data?.mostDiverseInventory && activeChart === "doughnut" ? (
                <CChartDoughnut
                  data={{
                    labels: data.mostDiverseInventory.map(
                      (item) => item.store_name
                    ),
                    datasets: [
                      {
                        backgroundColor: [
                          "#4BC0C0",
                          "#36A2EB",
                          "#FFCE56",
                          "#FF6384",
                          "#9966FF",
                        ],
                        data: data.mostDiverseInventory.map(
                          (item) => item.unique_products
                        ),
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        position: "right",
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            return `${context.label}: ${context.raw} products`;
                          },
                        },
                      },
                    },
                  }}
                />
              ) : (
                <CChartBar
                  data={{
                    labels: data?.mostDiverseInventory.map(
                      (item) => item.store_name
                    ),
                    datasets: [
                      {
                        label: "Unique Products",
                        backgroundColor: "#36A2EB",
                        data: data?.mostDiverseInventory.map(
                          (item) => item.unique_products
                        ),
                      },
                    ],
                  }}
                  options={{
                    indexAxis: "y",
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: "Number of Products",
                        },
                      },
                    },
                  }}
                />
              )}
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow className="align-items-center">
                <CCol>Stores with Highest Stock Volume</CCol>
                <CCol className="text-end">
                  <CFormSelect
                    size="sm"
                    style={{ width: "auto", display: "inline-block" }}
                    value={filters.timeRange}
                    onChange={(e) =>
                      setFilters({ ...filters, timeRange: e.target.value })
                    }
                  >
                    <option value="weekly">Last 7 Days</option>
                    <option value="biweekly">Last 14 Days</option>
                    <option value="monthly">Last 30 Days</option>
                    <option value="quarterly">Last 90 Days</option>
                  </CFormSelect>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              {data?.highestStockVolume && (
                <CChartBar
                  data={{
                    labels: data.highestStockVolume.map(
                      (item) => item.store_name
                    ),
                    datasets: [
                      {
                        label: "Total Inventory",
                        backgroundColor: "#4BC0C0",
                        data: data.highestStockVolume.map(
                          (item) => item.total_inventory
                        ),
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: "Total Stock Quantity",
                        },
                      },
                    },
                  }}
                />
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Monthly per-store chart moved to Stores analytics page */}

      {/* Stores Tables */}
      <CRow>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <span>Most Frequently Stocked Stores</span>
              <DownloadExcel
                rows={data?.frequentlyStocked || []}
                columns={[
                  { field: "store_name", headerName: "Store Name" },
                  { field: "visit_count", headerName: "Visit Count" },
                  { field: "last_visited", headerName: "Last Visited" },
                ]}
                excelName={`Frequently_Stocked_Stores_${
                  new Date().toISOString().split("T")[0]
                }`}
                buttonText="Export"
                size="sm"
              />
            </CCardHeader>
            <CCardBody>
              <CTable striped hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Store</CTableHeaderCell>
                    <CTableHeaderCell>Visit Count</CTableHeaderCell>
                    <CTableHeaderCell>Last Visited</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {data?.frequentlyStocked.map((store) => (
                    <CTableRow
                      key={store.store_id}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/stores/details/${store.store_id}`)
                      }
                    >
                      <CTableDataCell>{store.store_name}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge color="info">{store.visit_count}</CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        {new Date(store.last_visited).toLocaleDateString()}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <span>Stores with Out of Stock Products</span>
              <DownloadExcel
                rows={data?.outOfProducts || []}
                columns={[
                  { field: "store_name", headerName: "Store Name" },
                  {
                    field: "out_of_stock_items",
                    headerName: "Out of Stock Items",
                  },
                  { field: "last_checked", headerName: "Last Checked" },
                ]}
                excelName={`Out_of_Stock_Stores_${
                  new Date().toISOString().split("T")[0]
                }`}
                buttonText="Export"
                size="sm"
              />
            </CCardHeader>
            <CCardBody>
              <CTable striped hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Store</CTableHeaderCell>
                    <CTableHeaderCell>Out of Stock Items</CTableHeaderCell>
                    <CTableHeaderCell>Products</CTableHeaderCell>
                    <CTableHeaderCell>Last Checked</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {data?.outOfProducts.map((store) => (
                    <CTableRow
                      key={store.store_id}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/stores/details/${store.store_id}`)
                      }
                    >
                      <CTableDataCell>{store.store_name}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge color="danger">
                          {store.out_of_stock_items}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CTooltip content={store.product_names}>
                          <span
                            className="text-truncate d-inline-block"
                            style={{ maxWidth: "150px" }}
                          >
                            {store.product_names.split(",")[0]}
                            {store.product_names.split(",").length > 1 && "..."}
                          </span>
                        </CTooltip>
                      </CTableDataCell>
                      <CTableDataCell>
                        {new Date(store.last_checked).toLocaleDateString()}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Recent Activity Table */}
      <CRow>
        <CCol lg={12}>
          <CCard>
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <span>Recent Stock Activity</span>
              <DownloadExcel
                rows={data?.recentActivity || []}
                columns={[
                  { field: "store_name", headerName: "Store Name" },
                  { field: "products_updated", headerName: "Products Updated" },
                  {
                    field: "products_in_stock",
                    headerName: "Products In Stock",
                  },
                  { field: "last_updated", headerName: "Last Updated" },
                ]}
                excelName={`Recent_Stock_Activity_${
                  new Date().toISOString().split("T")[0]
                }`}
                buttonText="Export"
                size="sm"
              />
            </CCardHeader>
            <CCardBody>
              <CTable striped hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Store</CTableHeaderCell>
                    <CTableHeaderCell>Products Updated</CTableHeaderCell>
                    <CTableHeaderCell>Products In Stock</CTableHeaderCell>
                    <CTableHeaderCell>Stock Ratio</CTableHeaderCell>
                    <CTableHeaderCell>Last Updated</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {data?.recentActivity.map((store) => {
                    let inStockRatio =
                      parseInt(store.products_in_stock) /
                      parseInt(store.products_updated);
                    return (
                      <CTableRow
                        key={store.store_id}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/stores/details/${store.store_id}`)
                        }
                      >
                        <CTableDataCell>{store.store_name}</CTableDataCell>
                        <CTableDataCell>
                          {store.products_updated}
                        </CTableDataCell>
                        <CTableDataCell>
                          <CBadge color="success">
                            {store.products_in_stock}
                          </CBadge>
                        </CTableDataCell>
                        <CTableDataCell>
                          <CProgress
                            color={
                              inStockRatio > 0.8
                                ? "success"
                                : inStockRatio > 0.5
                                ? "warning"
                                : "danger"
                            }
                            value={inStockRatio * 100}
                            className="mb-3"
                          />
                        </CTableDataCell>
                        <CTableDataCell>
                          {new Date(store.last_updated).toLocaleDateString()}
                        </CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Filter Modal */}
      <StockFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />
    </div>
  );
};

export default StockAnalytics;
